import * as Sentry from '@sentry/nextjs';
import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Address } from 'viem';
import { ZodError } from 'zod';

import { ApiError } from '@/lib/errors/api';

import { liquiditySourcesEventHandlers, useLiquiditySourcesStore } from '@/store/liquiditySources';
import {
  recipientAddressSelector,
  useMarketTradeSettingsStore,
} from '@/store/tradeSettings/market';

import { SwapQuote } from '@/utils/0x.types';
import { getTokenPair } from '@/utils/amplitude';
import { zeroExApiFetcher } from '@/utils/fetcher';
import { ChainAddress, isEthereumAddress } from '@/utils/models';
import { routes } from '@/utils/routes';
import { withSentSlippage } from '@/utils/slippage/sentSlippage';
import { getFeeRequestParams } from '@/utils/swap-fee-params';

import { useTokenResult } from './tokenQueries/useTokenResult';
import { useSwapFeeQuery } from './transactionModule/useSwapFeeQuery';
import { useMatchaWallets } from './useMatchaWallets';
import { useResolvedSlippage } from './useResolvedSlippage';

export interface UseSwapQuoteParams {
  /** Token address to buy */
  buyTokenAddress: ChainAddress;
  buyTokenChainId: number;
  /** Token address to sell */
  sellTokenAddress: ChainAddress;
  sellTokenChainId: number;
  /** Amount of sell token in wei */
  sellAmount: string;
  /** Routes the gasless/standard split: a standard quote is not fetched while the trade is gasless. */
  isGasless: boolean;
  /** Buy-side amounts for the metrics/error context only; never used in the request. */
  telemetry: { buyAmountDecimal: string; buyAmountUsd: number };
}

export type SwapQuoteQueryOptions = Omit<
  UseQueryOptions<SwapQuote & { taker: Address }, ApiError | ZodError | Error>,
  'queryKey' | 'queryFn'
>;

/**
 * Fetches the swap `/quote` for a standard market order.
 */
export function useSwapQuote(
  params: UseSwapQuoteParams | null,
  options: SwapQuoteQueryOptions,
): UseQueryResult<SwapQuote & { taker: Address }, ApiError | ZodError | Error> {
  const {
    buyTokenAddress,
    buyTokenChainId,
    sellTokenAddress,
    sellTokenChainId,
    sellAmount,
    isGasless = false,
    telemetry: { buyAmountDecimal = '', buyAmountUsd = 0 } = {},
  } = params ?? {};

  const { token: sellTokenResult } = useTokenResult(sellTokenAddress, sellTokenChainId);
  const { token: buyTokenResult } = useTokenResult(buyTokenAddress, buyTokenChainId);
  const sellTokenSymbol = sellTokenResult?.symbol;
  const buyTokenSymbol = buyTokenResult?.symbol;

  Sentry.setTag('app.0x_api_version', 2);

  // get parameters
  const chainId = sellTokenChainId;
  const { ethereumWallet } = useMatchaWallets();
  const { getExcludedSources } = useLiquiditySourcesStore(liquiditySourcesEventHandlers);
  const excludedSources = chainId ? getExcludedSources(chainId) : '';
  const {
    bps: resolvedSlippageBps,
    isReady: isSlippageReady,
    isAuto,
  } = useResolvedSlippage({
    chainId: chainId || 1,
    sellToken: sellTokenResult,
    buyToken: buyTokenResult,
  });
  const { recipientAddress } = useMarketTradeSettingsStore(recipientAddressSelector);

  const { swapFeeBps, swapFeeRecipient, swapFeeToken } = useSwapFeeQuery({
    buyTokenAddress:
      buyTokenAddress && isEthereumAddress(buyTokenAddress) ? buyTokenAddress : undefined,
    buyTokenChainId,
    sellTokenAddress:
      sellTokenAddress && isEthereumAddress(sellTokenAddress) ? sellTokenAddress : undefined,
    sellTokenChainId,
  });

  // build URL
  const url = useMemo(() => {
    if (
      !chainId ||
      isGasless ||
      !sellAmount ||
      sellAmount === '0' ||
      !ethereumWallet ||
      !sellTokenAddress ||
      !isEthereumAddress(sellTokenAddress) ||
      !buyTokenAddress ||
      !isEthereumAddress(buyTokenAddress)
    )
      return null;

    const params = new URLSearchParams({
      chainId: chainId.toString(),
      buyToken: buyTokenAddress,
      sellToken: sellTokenAddress,
      sellAmount,
      taker: ethereumWallet.address,
      slippageBps: resolvedSlippageBps.toString(),
      ...getFeeRequestParams({ swapFeeBps, swapFeeRecipient, swapFeeToken }),
      ...(recipientAddress ? { recipient: recipientAddress } : {}),
    });
    if (excludedSources) params.set('excludedSources', excludedSources);

    return routes.api.SWAP_QUOTE + '?' + params.toString();
  }, [
    isGasless,
    sellAmount,
    chainId,
    buyTokenAddress,
    sellTokenAddress,
    resolvedSlippageBps,
    swapFeeBps,
    swapFeeRecipient,
    swapFeeToken,
    ethereumWallet,
    excludedSources,
    recipientAddress,
  ]);

  const pair = getTokenPair(
    chainId && sellTokenSymbol && sellTokenAddress
      ? { chainId, symbol: sellTokenSymbol, address: sellTokenAddress }
      : undefined,
    chainId && buyTokenSymbol && buyTokenAddress
      ? { chainId, symbol: buyTokenSymbol, address: buyTokenAddress }
      : undefined,
  );

  // build context for metrics & error reporting
  const context = useMemo(
    () => ({
      buy_token_address: buyTokenAddress,
      buy_token_amount: buyAmountDecimal,
      buy_token_symbol: buyTokenSymbol,
      chain_id: chainId,
      order_type: 'market_standard',
      pair,
      sell_token_address: sellTokenAddress,
      sell_token_amount: sellAmount,
      sell_token_symbol: sellTokenSymbol,
      usd_dollar_amount: buyAmountUsd,
      wallet_connected_address: ethereumWallet?.address ?? null,
      swap_fee_bps: swapFeeBps,
      swap_fee_recipient: swapFeeRecipient,
    }),
    [
      buyTokenAddress,
      buyAmountDecimal,
      buyAmountUsd,
      buyTokenSymbol,
      chainId,
      pair,
      sellTokenAddress,
      sellAmount,
      sellTokenSymbol,
      ethereumWallet?.address,
      swapFeeBps,
      swapFeeRecipient,
    ],
  );

  const { enabled: callerEnabled = true, ...restOptions } = options;
  return useQuery<SwapQuote & { taker: Address }, ApiError | ZodError | Error>({
    queryKey: ['swap-quote', url],
    queryFn: async () => withSentSlippage(await zeroExApiFetcher(url!, context), url!, isAuto),
    refetchOnReconnect: false,
    retry: false,
    ...restOptions,
    // Computed last so it overrides any `enabled` in `restOptions`; we
    // AND-merge so the leaf still self-disables when the URL guard fails
    // even if the caller passes `enabled: true`. `isSlippageReady` holds the
    // quote until Auto resolves, so it is fetched once at the final tolerance.
    enabled: url !== null && callerEnabled && isSlippageReady,
  });
}
